/**
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

// import type { AutocompleteState } from '@algolia/autocomplete-core';
import { searchClient } from '@algolia/client-search';
import '@docsearch/css/dist/style.css';
import type { DocSearchModalProps, DocSearchModal as DocSearchModalType } from '@docsearch/react';
import { DocSearchButton, useDocSearchKeyboardEvents } from '@docsearch/react';
// import type { InternalDocSearchHit, StoredDocSearchHit } from '@docsearch/react/dist/esm/types';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import { useEnvConfigModelStore } from '@/models/env-config/envConfig';
import { DocsSiteVisitType } from '@/shared/constant/DocsIdTypeData';
import { OnlineType } from '@/shared/constant/OnlineType';
import { isClient } from '@/shared/util/ssr';
import { useNavigate, useSearchParams } from '@remix-run/react';
import { useMount, usePersistFn } from 'ahooks';
import { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useGetLocalSearchData from './localSearch/useLocalSearchData';
import SearchHit, { handleClickSearchHit, ReIndexEntityType } from './SearchHit';
import useTranslations from './SearchTranslations';
import styles from './styles.module.less';
import { localSearchFallbackResponse } from './utils';

export type DocSearchProps = Omit<DocSearchModalProps, 'onClose' | 'initialScrollY'> & {
  forceOpen?: boolean;
  contextualSearch?: string;
  externalUrlRegex?: string;
  searchPagePath: boolean | string;
  onModalOpen?: () => void;
  onModalClose?: () => void;
  transformToLocalSearch?: boolean;
  localSearchConfig?: {
    localSearchIndexDataURL?: string;
    localSearchIndexSettingsURL?: string;
  };
};

function isRegexpStringMatch(regexAsString?: string, valueToTest?: string): boolean {
  if (typeof regexAsString === 'undefined' || typeof valueToTest === 'undefined') {
    return false;
  }

  return new RegExp(regexAsString, 'gi').test(valueToTest);
}

function debounce(func: (...args: any[]) => Promise<any>, wait = 100) {
  let lastTimeout: NodeJS.Timeout | null = null;
  return function (...args: any[]) {
    // @ts-ignore
    const that = this as any;
    return new Promise((resolve, reject) => {
      if (lastTimeout) {
        clearTimeout(lastTimeout);
      }
      lastTimeout = setTimeout(() => {
        lastTimeout = null;
        Promise.resolve(func.apply(that, args)).then(resolve).catch(reject);
      }, wait);
    });
  };
}

let DocSearchModal: typeof DocSearchModalType | null = null;

// type ResultsFooterProps = {
//   state: any;
//   onClose: () => void;
// };

// function ResultsFooter({ state, onClose }: ResultsFooterProps) {
//   const createSearchLink = useSearchLinkCreator();

//   return (
//     <Link to={createSearchLink(state.query)} onClick={onClose}>
//       <Translate id="theme.SearchBar.seeAll" values={{ count: state.context.nbHits }}>
//         {'See all {count} results'}
//       </Translate>
//     </Link>
//   );
// }

type FacetFilters = string[];

function mergeFacetFilters(f1: FacetFilters, f2: FacetFilters): FacetFilters {
  const normalize = (
    f: FacetFilters,
  ): readonly string[] | readonly (string | readonly string[])[] =>
    typeof f === 'string' ? [f] : f;
  return [...normalize(f1), ...normalize(f2)] as FacetFilters;
}

function DocSearch({
  contextualSearch,
  externalUrlRegex,
  onModalOpen,
  onModalClose,
  forceOpen,
  transformToLocalSearch,
  localSearchConfig,
  ...props
}: DocSearchProps) {
  const translations = useTranslations();
  // const {siteMetadata} = useDocusaurusContext();
  // const processSearchResultUrl = useSearchResultUrlProcessor();

  // const contextualSearchFacetFilters =
  //   useAlgoliaContextualFacetFilters() as FacetFilters;

  const configFacetFilters: FacetFilters = (props.searchParameters as any)?.facetFilters ?? [];

  // const facetFilters: FacetFilters = contextualSearch
  //   ? // Merge contextual search filters with config filters
  //     mergeFacetFilters(contextualSearchFacetFilters, configFacetFilters)
  //   : // ... or use config facetFilters
  //     configFacetFilters;

  const projectId = useDocsDataModelStore((x) => x.docsIdTypeData.projectId);

  const [urlSearchParameters] = useSearchParams();

  const branchId = useDocsDataModelStore((x) => {
    if (x.docsIdTypeData.onlineType === OnlineType.SharedDoc) {
      return undefined;
    }
    return x.docsIdTypeData.branchId;
  });

  const { onlineId, onlineType } = useDocsDataModelStore((x) => x.docsIdTypeData);
  const versionSettings = useDocsDataModelStore((x) => x.versionSettings);

  const facetFilters: FacetFilters = [
    ...configFacetFilters,
    `projectId:${projectId}`,
    `docsSiteId:${onlineId}`,
    `projectBranchId:${branchId}`,
    `docsSiteVersion:20241107`,
  ];

  // We let user override default searchParameters if she wants to
  const searchParameters: DocSearchProps['searchParameters'] = {
    ...(props.searchParameters as any),
    facetFilters,
    attributesToRetrieve: [
      'title',
      'type',
      'subtitle',
      'path',
      'description',
      'content',
      'tags',
      'objectID',
      'hierarchy',
    ],
    attributesToHighlight: ['title', 'subtitle', 'path', 'description', 'content', 'tags'],
    attributesToSnippet: ['content:10'],
    ranking: ['words', 'filters', 'typo', 'attribute', 'proximity', 'exact', 'custom'],
    snippetEllipsisText: '…',
    highlightPreTag: '<mark>',
    highlightPostTag: '</mark>',
    minWordSizefor1Typo: 3,
    minWordSizefor2Typos: 7,
    allowTyposOnNumericTokens: false,
    minProximity: 1,
    ignorePlurals: true,
    advancedSyntax: true,
    attributeCriteriaComputedByMinProximity: true,
    removeWordsIfNoResults: 'allOptional',
    hitsPerPage: 50,
  };

  // const history = useHistory();
  const searchContainer = useRef<HTMLDivElement | null>(null);
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [initialQuery, setInitialQuery] = useState<string | undefined>(undefined);

  const importDocSearchModalIfNeeded = useCallback(() => {
    if (DocSearchModal) {
      return Promise.resolve();
    }

    return Promise.all([
      // @ts-ignores
      import('@docsearch/react/modal') as Promise<typeof import('@docsearch/react')>,
    ]).then(([{ DocSearchModal: Modal }]) => {
      DocSearchModal = Modal;
    });
  }, []);

  const prepareSearchContainer = useCallback(() => {
    if (searchContainer.current) {
      return;
    }

    let divElement = document.body.querySelector('.' + styles['customized-docsearch-container']);
    if (divElement) {
      searchContainer.current = divElement as HTMLDivElement;
    } else {
      divElement = document.createElement('div');
      divElement.classList.add(styles['customized-docsearch-container'] ?? '');
      if (transformToLocalSearch) {
        divElement.classList.add(styles['customized-docsearch-container-local-search'] ?? '');
      }
      searchContainer.current = divElement as HTMLDivElement;
      document.body.insertBefore(divElement, document.body.lastChild);
    }
  }, []);

  const openModal = useCallback(() => {
    prepareSearchContainer();
    importDocSearchModalIfNeeded().then(() => {
      setIsOpen(true);
      onModalOpen?.();
      if (isClient) {
        setTimeout(() => {
          (
            searchContainer.current?.querySelector('#docsearch-input') as HTMLInputElement
          )?.focus?.();
        }, 500);
      }
    });
  }, [importDocSearchModalIfNeeded, prepareSearchContainer, onModalOpen]);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    searchButtonRef.current?.focus();
    onModalClose?.();
  }, [onModalClose]);

  useMount(() => {
    if (forceOpen) {
      openModal();
    }
  });

  const docsIdTypeData = useDocsDataModelStore((x) => x.docsIdTypeData);
  const urlRules = useDocsDataModelStore(
    (x) => x.projectSetting?.advancedSettings?.publishedDocUrlRules,
  );
  const seoInfos = useDocsDataModelStore((x) => x.seoInfos);
  const itemPathPrefix = useDocsDataModelStore((x) => x.itemPathPrefix);
  const navigate = useNavigate();

  const handleInput = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen) {
        return;
      }
      if (event.key === 'f' && (event.metaKey || event.ctrlKey)) {
        // ignore browser's ctrl+f
        return;
      }
      // prevents duplicate key insertion in the modal input
      event.preventDefault();
      setInitialQuery(event.key);
      openModal();
    },
    [isOpen, openModal],
  );

  const navigator = useRef({
    navigate({ itemUrl }: { itemUrl?: string }) {
      // Algolia results could contain URL's from other domains which cannot
      // be served through history and should navigate with window.location
      if (isRegexpStringMatch('^https?:\\/\\/', itemUrl)) {
        window.location.href = itemUrl!;
      } else {
        handleClickSearchHit(itemUrl!, {
          navigate,
          urlSearchParameters,
          docsIdTypeData,
          versionSettings,
          seoInfos,
          urlRules,
          itemPathPrefix: itemPathPrefix ?? {
            subdirectory: '',
            versionSlug: '',
            baseUrl: '',
            isSharedDocIndependentDomain: false,
          },
        });
      }
    },
  }).current;

  const findHighlightPath = usePersistFn((highlightResult: any, snippetResult: any) => {
    let result = null;
    let iterateTimes = 100;
    const iterateStack: any[] = [
      highlightResult.description,
      snippetResult.content,
      highlightResult.tags,
    ];
    while (iterateStack.length > 0 && iterateTimes > 0) {
      const task = iterateStack.pop();
      if (!task) {
        continue;
      }
      if (Array.isArray(task)) {
        iterateStack.push(...task);
        iterateTimes = iterateTimes - 1;
        continue;
      }
      if (typeof task !== 'object') {
        iterateTimes = iterateTimes - 1;
        continue;
      }
      if (task.matchLevel === 'full') {
        result = task.value;
        break;
      }
      if (task.matchLevel === 'partial') {
        result = task.value;
        continue;
      }
      iterateStack.push(...Object.values(task));
      iterateTimes = iterateTimes - 1;
    }
    return {
      isPathMatch:
        (highlightResult.path?.matchLevel === 'full' ||
          highlightResult.path?.matchLevel === 'partial') &&
        !!highlightResult.path?.value,
      pathMatchValue: highlightResult.path?.value,
      isDescriptionMatch:
        (highlightResult.description?.matchLevel === 'full' ||
          highlightResult.description?.matchLevel === 'partial') &&
        !!highlightResult.description?.value,
      isTagsMatch:
        (highlightResult.tags?.matchLevel === 'full' ||
          highlightResult.tags?.matchLevel === 'partial') &&
        !!highlightResult.tags?.value,
      fallbackMatchValue: result,
    };
  });

  const transformItems = useRef<DocSearchModalProps['transformItems']>((items) =>
    items.map((item: any) => {
      const highlightResult: any = item._highlightResult || {};

      const decoratedSubtitle =
        !!highlightResult.subtitle?.value &&
        !!highlightResult.subtitle?.matchLevel &&
        highlightResult.subtitle?.matchLevel !== 'none'
          ? highlightResult.subtitle.value
          : item.subtitle;

      const decoratedTitle =
        !!highlightResult.title?.value &&
        !!highlightResult.title?.matchLevel &&
        highlightResult.title?.matchLevel !== 'none'
          ? highlightResult.title.value
          : item.title;

      const {
        isPathMatch,
        pathMatchValue,
        fallbackMatchValue: pathSubtitle,
        isDescriptionMatch,
        isTagsMatch,
      } = findHighlightPath(highlightResult, item._snippetResult || highlightResult);

      // lvl1 表示 title 或 subtitle 有 match、lvl2 表示从属于某个目录（这个目录的名字有 match），content 表示只是内容 match
      const showAsFolderChildHierarchy =
        item.type !== ReIndexEntityType.API_FOLDER && isPathMatch && pathMatchValue;
      const showAsContentLevel = !showAsFolderChildHierarchy && !isDescriptionMatch && !isTagsMatch;
      const calcHierarchyKey = showAsFolderChildHierarchy
        ? 'lvl2'
        : showAsContentLevel
          ? 'content'
          : 'lvl1';
      // nonNullContent from decoratedSubtitle, pathSubtitle, item description
      const nonNullContent = pathSubtitle || decoratedSubtitle || item.description || item.tags;
      // final content have suffix
      const content = nonNullContent + (calcHierarchyKey === 'lvl2' ? ` - ${pathMatchValue}` : '');

      // if content field does not need subtitle, then add subtitle to title
      const isSubtitleShowedAsContent = !pathSubtitle && !!decoratedSubtitle;
      const title =
        decoratedTitle +
        (!isSubtitleShowedAsContent &&
        [
          ReIndexEntityType.ENDPOINT,
          ReIndexEntityType.CUSTOM_ENDPOINT,
          ReIndexEntityType.WEB_SOCKET_API,
        ].includes(item.type)
          ? ` - ${decoratedSubtitle}`
          : '');
      return {
        ...item,
        title,
        // 层级样式
        type: calcHierarchyKey,
        hierarchy: showAsFolderChildHierarchy
          ? {
              // 一级标题
              lvl1: pathMatchValue,
              lvl2: title,
            }
          : showAsContentLevel
            ? {
                lvl1: content,
              }
            : {
                lvl1: title,
              },
        // 其他层级的匹配这里对应是二级标题，content 层级这个内容对应的是 一级标题
        content: showAsContentLevel ? title : content,
        url: item.objectID,
        url_without_anchor: item.objectID,
        anchor: null,
      };
    }),
  ).current;

  // const resultsFooterComponent: DocSearchProps['resultsFooterComponent'] = useMemo(
  //   () =>
  //     // eslint-disable-next-line react/no-unstable-nested-components
  //     (footerProps: Omit<ResultsFooterProps, 'onClose'>): JSX.Element =>
  //       <ResultsFooter {...footerProps} onClose={closeModal} />,
  //   [closeModal],
  // );

  const transformSearchClient = useCallback(() => {
    const client = searchClient(props.appId, props.apiKey, {
      timeouts: {
        connect: 15000,
        read: 15000,
        write: 15000,
      },
    });
    // eslint-disable-next-line no-param-reassign
    client.search = debounce(client.search, 500) as any;

    return client;
  }, []);

  const { getLocalSearchIndexData, getLocalSearchIndexSettings } = useGetLocalSearchData();
  const visitType = useDocsDataModelStore((x) => {
    if (x.docsIdTypeData.onlineType === OnlineType.SharedDoc) {
      return DocsSiteVisitType.WithId;
    }
    return x.docsIdTypeData.visitType;
  });
  const subdirectory = useDocsDataModelStore((x) => {
    if (x.docsIdTypeData.onlineType === OnlineType.SharedDoc) {
      return '';
    }
    return x.docsIdTypeData.subdirectory;
  });
  const envConfig = useEnvConfigModelStore((x) => x);

  const localSearchRequester = async (req: any) => {
    try {
      if (
        !localSearchConfig?.localSearchIndexDataURL ||
        !localSearchConfig?.localSearchIndexSettingsURL
      )
        return localSearchFallbackResponse;

      const requests = JSON.parse(req.data).requests;
      const requestOptions: Parameters<typeof getLocalSearchIndexData>[1] = {
        visitType,
        subdirectory,
        envConfig,
      };
      const indexData = await getLocalSearchIndexData(
        localSearchConfig.localSearchIndexDataURL,
        requestOptions,
      );
      if (!indexData) return localSearchFallbackResponse;

      const indexSettings = await getLocalSearchIndexSettings(
        localSearchConfig.localSearchIndexSettingsURL,
        requestOptions,
      );

      const { default: executeLocalSearch } = await import('./localSearch/searchIndex');

      const searchResult = executeLocalSearch(requests, indexData, indexSettings);
      return {
        status: 200,
        content: JSON.stringify(searchResult),
        isTimedOut: false,
      };
    } catch (error) {
      console.error(error);
      return localSearchFallbackResponse;
    }
  };

  const transformLocalSearchClient = useCallback(() => {
    const client = searchClient('appId', 'apiKey', {
      timeouts: {
        connect: 15000,
        read: 15000,
        write: 15000,
      },
      requester: {
        send: localSearchRequester,
      },
    });

    const originalSearch = client.search;
    client.search = debounce(originalSearch, 500) as any;

    return client;
  }, []);

  useDocSearchKeyboardEvents({
    isOpen,
    onOpen: openModal,
    onClose: closeModal,
    onInput: handleInput,
    searchButtonRef,
  });

  return (
    <>
      {/* <Head> */}
      {/* This hints the browser that the website will load data from Algolia,
        and allows it to preconnect to the DocSearch cluster. It makes the first
        query faster, especially on mobile. */}
      {/* <link
          rel="preconnect"
          href={`https://${props.appId}-dsn.algolia.net`}
          crossOrigin="anonymous"
        />
      </Head> */}

      {forceOpen ? null : (
        <div className={styles['customized-docsearch-button']}>
          <DocSearchButton
            onTouchStart={importDocSearchModalIfNeeded}
            onFocus={importDocSearchModalIfNeeded}
            onMouseOver={importDocSearchModalIfNeeded}
            onClick={openModal}
            ref={searchButtonRef}
            translations={translations.button}
          />
        </div>
      )}

      {isOpen &&
        DocSearchModal &&
        searchContainer.current &&
        createPortal(
          <DocSearchModal
            onClose={closeModal}
            initialScrollY={window.scrollY}
            initialQuery={initialQuery}
            navigator={navigator}
            transformItems={transformItems}
            disableUserPersonalization
            transformSearchClient={
              transformToLocalSearch ? transformLocalSearchClient : transformSearchClient
            }
            maxResultsPerGroup={50}
            hitComponent={
              SearchHit as unknown as React.ComponentProps<typeof DocSearchModal>['hitComponent']
            }
            // transformSearchClient={transformSearchClient}
            // {...(props.searchPagePath && {
            //   resultsFooterComponent,
            // })}
            {...props}
            searchParameters={searchParameters}
            placeholder={translations.placeholder}
            translations={translations.modal}
          />,
          searchContainer.current,
        )}
    </>
  );
}

export default DocSearch;
